/**
 * /es/:slug — Spanish dynamic service-area page.
 *
 * Mirrors /:slug ServiceAreaPage but pulls from /api/service-areas/page-by-slug-es
 * which returns the translation_es subdoc generated by the template-based ES
 * generator in backend/routes/service_areas.py.
 *
 * Catches every ES slug like /es/implantes-dentales-arlington and renders it
 * with the SpanishHeader/Footer (provided by the parent layout) and EsSEO.
 */
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import EsSEO from '@/components/seo/EsSEO';
import SpaRedirect from '@/components/seo/SpaRedirect';
import { registerServiceAreaSlugMapping } from '@/i18n/routeMap';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SpanishServiceAreaPage = () => {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    setPage(null);
    fetch(`${API_URL}/api/service-areas/page-by-slug-es/${encodeURIComponent(slug)}`)
      .then((res) => {
        if (res.status === 404) {
          setNotFound(true);   // genuine 404 -> noindex
          return null;
        }
        if (!res.ok) {
          return null;         // transient (5xx) -> keep loading, NO noindex
        }
        return res.json();
      })
      .then((data) => {
        if (!data) return;
        setPage(data);
        if (data.full_slug && data.en_full_slug) {
          registerServiceAreaSlugMapping(data.en_full_slug, data.full_slug);
        }
      })
      .catch(() => { /* transient network error -> do NOT noindex */ })
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div data-testid="es-service-area-loading" className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600" />
      </div>
    );
  }

  if (notFound) {
    // definitive 404 only -> clean redirect to the ES areas hub
    return <SpaRedirect to="/es/areas-que-atendemos" message="Redirigiendo…" />;
  }

  if (!page) {
    // Transient error: neutral loading, NO noindex.
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600" />
      </div>
    );
  }

  const enHref = page.en_full_slug ? `/${page.en_full_slug}` : '/';

  return (
    <>
      <EsSEO
        title={page.title}
        description={page.meta_description}
        canonical={`/es/${page.full_slug}`}
        hreflangEn={enHref}
        schema={page.schema_json}
      />

      <div className="min-h-screen bg-white" data-testid="es-service-area-page">
        {/* Hero */}
        <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white py-16">
          <div className="max-w-4xl mx-auto px-4">
            <div className="flex items-center gap-2 text-teal-100 text-sm mb-4">
              <Link to="/es" className="hover:text-white">Inicio</Link>
              <span>/</span>
              <Link to="/es/areas-que-atendemos" className="hover:text-white">Áreas que Atendemos</Link>
              <span>/</span>
              <span className="text-white">{page.city}</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="es-service-area-h1">
              {page.h1}
            </h1>
            <p className="text-teal-50 text-lg">
              A solo {page.distance_miles} minutos de {page.city} • Más de 1,090 reseñas de 5 estrellas
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div
            className="prose prose-lg max-w-none"
            dangerouslySetInnerHTML={{ __html: page.content }}
          />
        </div>

        {/* Related Services (Spanish) */}
        <div className="bg-gray-50 py-12">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">
              Más Servicios para Residentes de {page.city}
            </h2>
            <div className="grid md:grid-cols-4 gap-4">
              <Link to="/es/implantes-dentales" className="bg-white p-4 rounded-lg shadow hover:shadow-md transition">
                <h3 className="font-semibold text-teal-600">Implantes Dentales</h3>
                <p className="text-sm text-gray-600">Reemplazo permanente de dientes</p>
              </Link>
              <Link to="/es/invisalign" className="bg-white p-4 rounded-lg shadow hover:shadow-md transition">
                <h3 className="font-semibold text-teal-600">Invisalign</h3>
                <p className="text-sm text-gray-600">Alineadores transparentes</p>
              </Link>
              <Link to="/es/lanap" className="bg-white p-4 rounded-lg shadow hover:shadow-md transition">
                <h3 className="font-semibold text-teal-600">Tratamiento LANAP</h3>
                <p className="text-sm text-gray-600">Terapia láser para encías</p>
              </Link>
              <Link to="/es/carillas-first-fit" className="bg-white p-4 rounded-lg shadow hover:shadow-md transition border-2 border-teal-100">
                <h3 className="font-semibold text-teal-600">Carillas First Fit</h3>
                <p className="text-sm text-gray-600">Carillas a la medida el mismo día</p>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SpanishServiceAreaPage;
